import { useSelector } from '@xstate/react';
import { useLocation } from 'react-router';
import { Tabs } from '~/components';
import { ClaimsView } from './claims';
import { CreateMalpracticeClaimFormValues, MalpracticeClaimFormValues } from './claims/types';
import { InsuranceView } from './insurance';
import {
  CreateMalpracticeInsuranceFormValues,
  MalpracticeInsuranceFormValues,
} from './insurance/types';
import { useInsuranceCertificateUpload } from './insurance/useInsuranceCertificateUpload';
import { MALPRACTICE_TABS, useMalpracticeActorRef } from './utils';

export const MalpracticeTabLayout = () => {
  const location = useLocation();
  const hashTab = location.hash.replace('#', '');
  const malpracticeRef = useMalpracticeActorRef();
  const { activeTab, claimsRef, insurancesRef } = useSelector(
    malpracticeRef,
    (state) => state.context
  );
  const {
    data: claimsData,
    loading: claimsLoading,
    modal: claimsModal,
    form: claimsForm,
  } = useSelector(claimsRef, (state) => state.context);

  const {
    data: insuranceData,
    loading: insuranceLoading,
    modal: insuranceModal,
    form: insuranceForm,
  } = useSelector(insurancesRef, (state) => state.context);

  const certificateUpload = useInsuranceCertificateUpload();

  const handleTabChange = (index: number) =>
    malpracticeRef.send({ type: 'ON_SET_ACTIVE_TAB', index });

  const currentTab = hashTab == 'claim' ? MALPRACTICE_TABS[1] : MALPRACTICE_TABS[activeTab];

  const claimProps = {
    data: claimsData,
    form: claimsForm,
    loading: claimsLoading,
    create: claimsModal.create,
    edit: claimsModal.edit,
    handleEdit: (id: number) => claimsRef.send({ type: 'ON_EDIT', payload: { id } }),
    triggerAddModal: () => claimsRef.send({ type: 'ON_ADD' }),
    handleDelete: (id: number) =>
      claimsRef.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id } }),
    handleClose: () => claimsRef.send({ type: 'ON_CLOSE_MODAL' }),
    handleSort: (sortBy: string) => claimsRef.send({ type: 'ON_SORT_TABLE', sortBy }),
    addClaim: (values: CreateMalpracticeClaimFormValues) =>
      claimsRef.send({ type: 'ON_SUBMIT', values: values }),
    editClaim: (values: MalpracticeClaimFormValues) =>
      claimsRef.send({ type: 'ON_UPDATE', values: values }),
  };

  const insuranceProps = {
    data: insuranceData,
    form: insuranceForm,
    loading: insuranceLoading,
    create: insuranceModal.create,
    edit: insuranceModal.edit,
    handleEdit: (id: number) => insurancesRef.send({ type: 'ON_EDIT', payload: { id } }),
    triggerAddModal: () => insurancesRef.send({ type: 'ON_ADD' }),
    handleDelete: (id: number) =>
      insurancesRef.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id } }),
    handleClose: () => insurancesRef.send({ type: 'ON_CLOSE_MODAL' }),
    addInsurance: (values: CreateMalpracticeInsuranceFormValues) =>
      insurancesRef.send({ type: 'ON_SUBMIT', values: values }),
    editInsurance: (values: MalpracticeInsuranceFormValues) =>
      insurancesRef.send({ type: 'ON_UPDATE', values: values }),
    ...certificateUpload,
  };

  return (
    <div>
      <Tabs
        tabs={MALPRACTICE_TABS}
        handleTabChange={handleTabChange}
        activeTab={MALPRACTICE_TABS.findIndex((tab) => tab === currentTab)}
      />
      {currentTab.state === '$_INSURANCES_TAB' ? (
        <InsuranceView {...insuranceProps} />
      ) : (
        <ClaimsView {...claimProps} />
      )}
    </div>
  );
};
